import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { HeaderDto } from 'src/app/dto/header.dto';
import { OrderChangesService } from 'src/app/services/order/order-changes.service';
import { Order } from 'src/app/dto/order.dto';
import { TtnGeneratorService } from 'src/app/services/docs-service/ttn-generator.service';
import { TrnGeneratorService } from 'src/app/services/docs-service/trn-generator.service';
import {
  RtsDay,
  RtsLoadingData,
  RtsVehicle
} from 'src/app/dto/get-rts-result.dto';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { EditCarComponent } from '../edit-car/edit-car.component';
import { SdizNumberValidatorService } from '../../../../services/sdiz-number-validator.service';
import { DocumentsStorageService } from '../../../../services/documents-storage.service';
import { ExcelGeneratorData } from '../../../../dto/excel-generation-data.dto';
import { DateTimeService } from '../../../../services/docs-service/date-time.service';
import { OrderProviderService } from '../../../../services/order/order-provider.service';

export interface LoadCarForm {
  tareWeight: FormControl<number | null>;
  grossWeight: FormControl<number | null>;
  netWeight: FormControl<number | null>;
  quarantineCertificateNumber: FormControl<string | null>;
  quarantineCertificateDate: FormControl<string | null>;
  sdizNumber: FormControl<string | null>;
  declarationOfConformityNumber: FormControl<string | null>;
  declarationOfConformityDate: FormControl<string | null>;
}

@Component({
  selector: 'app-load-car',
  templateUrl: './load-car.component.html',
  styleUrls: ['./load-car.component.scss']
})
/**
 * Экран ввода данных погрузки для одного рейса дня: вес (тара/брутто → нетто), номера сертификатов,
 * СДИЗ, декларация. По этим данным формируются ТТН и ТРН. Ключевое для оффлайна:
 * - правки погрузки сохраняются в локальную рабочую копию заказа ({@code updateVehicle} →
 *   {@code updateRequests}), а сгенерированные документы — в очередь на выгрузку
 *   ({@link #storeDocumentsIfNecessary} при уходе с экрана);
 * - сохранение при уходе ({@link #handleRouteChanges}, {@code ngOnDestroy}) идёт ТОЛЬКО при валидной
 *   ИЗМЕНЁННОЙ форме — чтобы случайная очистка поля не затёрла ранее введённые данные;
 * - номер ТТН привязан к рейсу, а не к позиции машины в списке (см. {@link #updateTtnId}).
 */
export class LoadCarComponent implements OnInit, OnDestroy {
  public canProcess: boolean = true;
  headerDataValues: HeaderDto = {
    title: 'День '
  };

  carInfo: RtsVehicle;
  carIndex: number;
  ttnId: string;
  vehicleId: string = '';
  requestDay: string = '';
  formGroup: FormGroup;
  targetUrl = window.location.pathname.split('/')[1];
  currentDayId: string;
  order: Order;
  orderId: number;
  phoneHref: string;
  emptyTrailerRegistrationNumberError: boolean = false;
  ttnDocumentGeneratingButtonSpiner: boolean = false;
  trnDocumentGeneratingButtonSpiner: boolean = false;
  defaultBackUrl: string;
  loading: boolean;

  private readonly destroy$ = new Subject<void>();

  private shouldStoreDocuments: boolean;
  private rtsDay: RtsDay | null;

  constructor(
    private modalCtrl: ModalController,
    private route: ActivatedRoute,
    private orderChangesService: OrderChangesService,
    private ttnGeneratorService: TtnGeneratorService,
    private trnGeneratorService: TrnGeneratorService,
    private readonly sdizValidatorService: SdizNumberValidatorService,
    private readonly router: Router,
    private readonly docStorageService: DocumentsStorageService,
    private readonly dateTimeService: DateTimeService,
    private readonly orderProvider: OrderProviderService,
    private navController: NavController
  ) {}

  ngOnInit() {
    this.handleRouteChanges();
    this.defaultBackUrl = window.location.pathname
      .split('/')
      .slice(0, -2)
      .join('/');
    this.orderId = this.route.snapshot.parent?.parent?.params['id'];
    // carIndex (порядковый номер машины в списке, начинается с 1) больше НЕ участвует в формировании
    // номера ТТН (см. resolveTtnId) — служит лишь признаком корректного перехода с экрана дня: при
    // прямой навигации без него возвращаемся назад.
    this.carIndex = this.route.snapshot.queryParams['carIndex'];
    if (!this.carIndex) {
      this.navController.back();
    }
    this.currentDayId =
      window.location.pathname.split('/')[
        window.location.pathname.split('/').findIndex(el => el === 'day') + 1
      ];

    this.vehicleId =
      decodeURIComponent(
      window.location.pathname.split('/')[
      window.location.pathname
        .split('/')
        .findIndex(el => el === 'load-car') + 2
        ]
      );

    this.requestDay =
      window.location.pathname.split('/')[
        window.location.pathname.split('/').findIndex(el => el === 'day') + 1
      ];

    this.formGroup = new FormGroup<LoadCarForm>({
      tareWeight: new FormControl<number | null>(null, Validators.required),
      grossWeight: new FormControl<number | null>(null, Validators.required),
      netWeight: new FormControl<number | null>(null, Validators.required),
      quarantineCertificateNumber: new FormControl<string | null>(
        null,
        Validators.required
      ),
      quarantineCertificateDate: new FormControl<string | null>(null, [
        Validators.required
      ]),
      sdizNumber: new FormControl<string | null>(null, [
        Validators.required,
        this.sdizValidatorService.createValidator()
      ]),
      declarationOfConformityNumber: new FormControl<string | null>(
        null,
        Validators.required
      ),
      declarationOfConformityDate: new FormControl<string | null>(null, [
        Validators.required
      ])
    });

    this.formGroup
      .get('tareWeight')
      ?.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((tareWeight: number) => {
        this.updateNetWeightIfValid(
          tareWeight,
          this.formGroup.get('grossWeight')?.value
        );
      });

    this.formGroup
      .get('grossWeight')
      ?.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((grossWeight: number) => {
        this.updateNetWeightIfValid(
          this.formGroup.get('tareWeight')?.value,
          grossWeight
        );
      });

    this.orderProvider
      .loadOrder(this)
      .pipe(takeUntil(this.destroy$))
      .subscribe(success => {
        if (success) {
          const dayData = this.order.getDayById(this.currentDayId);
          this.rtsDay = dayData;
          if (dayData) {
            const dayNumber = this.order.getDayNumberById(dayData.orderNumber);
            this.headerDataValues.title = `День ${dayNumber}`;
            this.updateTtnId();
          }
          this.getVehicleDetails(this.vehicleId);

          this.canProcess = this.order.canProcessDay(this.currentDayId);
          if (!this.canProcess) {
            this.formGroup.disable();
          }

          if (
            this.emptyTrailerRegistrationNumberError &&
            this.carInfo.trailerRegistrationNumber &&
            !this.carInfo.hasTrailer
          ) {
            this.emptyTrailerRegistrationNumberError = false;
          }
        }
      });

    if (this.canProcess) {
      this.handleFormGroupChanges();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    // Сохраняем прогресс при уходе с экрана ТОЛЬКО если форма изменена И валидна. Прежде сохранение шло
    // безусловно (getRawValue без проверок): при случайной очистке поля и выходе введённые ранее данные
    // погрузки (вес/номера) перетирались null-ами. Пустой/невалидный ввод наружу не сохраняем.
    if (this.canProcess && this.formGroup.dirty && this.formGroup.valid) {
      const updatedRequest = this.order?.updateVehicle(
        this.requestDay,
        this.vehicleId,
        this.ttnId,
        this.formGroup.getRawValue() as RtsLoadingData
      );
      this.orderChangesService.updateRequests(updatedRequest, this.targetUrl);
    }
  }

  /** Пересчёт нетто = брутто − тара при валидных значениях; брутто ≤ тары помечается ошибкой формы. */
  updateNetWeightIfValid(tareWeight: number, grossWeight: number) {
    if (tareWeight && grossWeight) {
      if (grossWeight > tareWeight) {
        const netWeight = grossWeight - tareWeight;
        this.formGroup.get('netWeight')?.setValue(netWeight);
        this.formGroup.get('grossWeight')?.setErrors(null);
      } else {
        this.formGroup.get('netWeight')?.reset();
        this.formGroup
          .get('grossWeight')
          ?.setErrors({ grossLessThanTare: true });
      }
    }
  }

  getVehicleDetails(vehicleId: string): void {
    const vehicle: RtsVehicle | null = this.order?.getVehicleById(
      vehicleId,
      this.currentDayId
    );
    if (vehicle) {
      this.carInfo = vehicle;
      this.phoneHref = `tel:${this.carInfo.driverPhone}`;

      if (vehicle?.loadingData?.lastUpdate) {
        const {
          tareWeight,
          grossWeight,
          netWeight,
          quarantineCertificateDate,
          quarantineCertificateNumber,
          sdizNumber,
          declarationOfConformityNumber,
          declarationOfConformityDate
        } = vehicle.loadingData;

        this.formGroup.patchValue(
          {
            tareWeight,
            grossWeight,
            netWeight,
            quarantineCertificateNumber,
            quarantineCertificateDate,
            sdizNumber,
            declarationOfConformityNumber,
            declarationOfConformityDate
          },
          { emitEvent: false }
        );
      }
    }
  }

  /**
   * «Скачать ТТН»: сохраняем введённые данные погрузки в рабочую копию заказа ({@code updateVehicle}),
   * генерируем и открываем документ, затем публикуем правку ({@code updateRequests} — персист + поток).
   * В очередь на выгрузку документ попадёт при уходе с экрана ({@link #storeDocumentsIfNecessary}).
   */
  async generateTtn() {
    // Страховка помимо [disabled] у кнопки: при невалидной форме не генерируем документ и не пишем
    // getRawValue() в loadingData — иначе null-веса и т.п. привели бы к TypeError в генераторе
    // (grossWeight.toLocaleString) и к перезаписи ранее введённых данных погрузки.
    if (this.formGroup.invalid) {
      return;
    }
    const updatedRequest = this.order.updateVehicle(
      this.requestDay,
      this.vehicleId,
      this.ttnId,
      this.formGroup.getRawValue() as RtsLoadingData
    );
    this.ttnDocumentGeneratingButtonSpiner = true;
    try {
      await this.ttnGeneratorService.getTtn(updatedRequest, this.carInfo, this.currentDayId);
    } catch (error) {
      Sentry.captureException(error);
      // console.error('Error handling Excel file:', error);
    } finally {
      this.ttnDocumentGeneratingButtonSpiner = false;
    }

    this.orderChangesService.updateRequests(updatedRequest, this.targetUrl);
  }

  /** «Скачать ТРН»: то же, что {@link #generateTtn}, но для транспортной накладной. */
  async generateTrn() {
    // Та же страховка, что и в generateTtn (см. комментарий там).
    if (this.formGroup.invalid) {
      return;
    }
    const updatedRequest = this.order.updateVehicle(
      this.requestDay,
      this.vehicleId,
      this.ttnId,
      this.formGroup.getRawValue() as RtsLoadingData
    );

    this.trnDocumentGeneratingButtonSpiner = true;
    try {
      await this.trnGeneratorService.getTrn(this.order, this.carInfo, this.currentDayId);
    } catch (error) {
      Sentry.captureException(error);
      // console.error('Error handling Excel file:', error);
    } finally {
      this.trnDocumentGeneratingButtonSpiner = false;
    }

    this.orderChangesService.updateRequests(updatedRequest, this.targetUrl);
  }

  async openRTCActions() {
    const modal = await this.modalCtrl.create({
      component: EditCarComponent,
      componentProps: {
        editVehicleData: this.carInfo,
        filltrailerRegistrationNumber: this.emptyTrailerRegistrationNumberError
      }
    });
    modal.onDidDismiss().then(event => {
      if (event.role !== 'cancel') {
        this.shouldStoreDocuments = true;
      }
    });
    await modal.present();
  }

  // Любая правка формы взводит флаг shouldStoreDocuments: значит, при уходе с экрана надо перегенерировать
  // и положить в очередь на выгрузку актуальные ТТН/ТРН (см. storeDocumentsIfNecessary).
  private handleFormGroupChanges(): void {
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.shouldStoreDocuments = true;
    });
  }

  /**
   * Сохранение документов при УХОДЕ с этого экрана. Ловим первый {@code NavigationEnd}, уводящий с
   * текущего URL, и складываем актуальные ТТН/ТРН в очередь на выгрузку. Отдельно от {@code ngOnDestroy}:
   * ловит переход раньше уничтожения компонента (в т.ч. когда данные ещё под рукой).
   */
  private handleRouteChanges(): void {
    const thisUrl: string = this.router.url;
    let lastUrl: string = thisUrl;
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(async event => {
        if (event instanceof NavigationEnd) {
          if (event.urlAfterRedirects !== thisUrl && lastUrl === thisUrl) {
            await this.storeDocumentsIfNecessary();
          }
          lastUrl = event.urlAfterRedirects;
        }
      });
  }

  /**
   * Кладёт актуальные ТТН и ТРН рейса в очередь на выгрузку ({@code documents-storage}) — но только если
   * есть что сохранять и данные валидны: день доступен для обработки ({@code canProcess}), флаг взведён
   * ({@code shouldStoreDocuments}) и форма валидна. Иначе выходим, не трогая уже сохранённое (невалидная
   * форма не должна затирать введённые данные). После успеха флаг сбрасываем.
   */
  private async storeDocumentsIfNecessary(): Promise<void> {
    if (!this.canProcess) {
      return;
    }
    if (!this.shouldStoreDocuments || this.formGroup.invalid) {
      return;
    }
    const updatedRequest = this.order.updateVehicle(
      this.requestDay,
      this.vehicleId,
      this.ttnId,
      this.formGroup.getRawValue() as RtsLoadingData
    );
    const day: RtsDay | null = updatedRequest.getDayById(this.currentDayId);
    if (!day) {
      throw new Error('Невозможно найти запись РТС на текущий день!');
    }
    await Promise.all([
      this.storeTrnDocumentData(day),
      this.storeTtnDocumentData(day)
    ]);
    this.shouldStoreDocuments = false;
  }

  // Генерирует структуру данных ТРН и кладёт документ в очередь на выгрузку (ключ — рейс в дне заявки).
  private async storeTrnDocumentData(day: RtsDay): Promise<void> {
    const docData: ExcelGeneratorData =
      await this.trnGeneratorService.generateTrnDataStructure(
        this.order,
        this.carInfo,
        this.currentDayId,
      );
    await firstValueFrom(
      this.docStorageService.storeTrn(docData, {
        requestId: this.order.id,
        orderNumber: day.orderNumber,
        vehicleDataId: String(this.carInfo.id)
      })
    );
  }

  // Генерирует структуру данных ТТН и кладёт документ в очередь на выгрузку (ключ — рейс в дне заявки).
  private async storeTtnDocumentData(day: RtsDay): Promise<void> {
    const docData: ExcelGeneratorData =
      await this.ttnGeneratorService.generateTtnDataStructure(
        this.order,
        this.carInfo,
        this.currentDayId,
      );
    await firstValueFrom(
      this.docStorageService.storeTtn(docData, {
        requestId: this.order.id,
        orderNumber: day.orderNumber,
        vehicleDataId: String(this.carInfo.id)
      })
    );
  }

  private updateTtnId(): void {
    if (!this.rtsDay) {
      return;
    }
    const shipmentDateStr = this.rtsDay.shipmentDate;
    const shipmentDate: Date =
      shipmentDateStr == null
        ? new Date()
        : this.dateTimeService.parseDateFromString(shipmentDateStr);
    // Номер ТТН привязан к рейсу (стабилен и уникален на рейс), а не к позиции машины в списке (D10):
    // resolveTtnId идемпотентен на рейс и присваивает max(присвоенных в дне)+1 для нового рейса.
    this.ttnId = this.ttnGeneratorService.resolveTtnId(
      this.order.id,
      shipmentDate,
      this.rtsDay.vehicles,
      this.vehicleId
    );
  }
}
